/**
 * Smilodox Secret Sale — Shopify validation probe (Gloura behavior port).
 * Empty-cart path: add uniquely marked probe line → cart/update discount →
 * require applicable===true → scrub that exact probe line (verify before release).
 * Single-flight. Checkout blocked while probe/scrub/unclean recovery is in progress.
 *
 * Cross-page / close-tab recovery:
 * - sessionStorage marker for same-tab navigation (immediate)
 * - mirrored localStorage token+timestamp for tab close/reopen
 * - other tabs recover only when the persistent marker is stale (no race with an
 *   active probe). Tiny global bootstrap loads this file only when needed.
 */
(function () {
  "use strict";

  // Strict singleton: section + on-demand bootstrap must never double-bind.
  if (window.SmilodoxSecretSaleProbe || window.__smilodoxSecretSaleProbeBooted) return;
  window.__smilodoxSecretSaleProbeBooted = true;

  const PROBE_LINE_PROPERTY = "_smilodox_secret_sale_probe";
  const STORAGE_PROBE_VARIANT = "smilodox:secret-sale:probe-variant";
  const STORAGE_VALIDATION_PREFIX = "smilodox:secret-sale:validation:";
  const STORAGE_PROBE_DONE_PREFIX = "smilodox:secret-sale:probe-done:";
  const STORAGE_CANDIDATE = "smilodox:secret-sale:candidate";
  const STORAGE_RECOVERY_MARKER = "smilodox:secret-sale:probe-pending";
  const STORAGE_RECOVERY_PERSIST = "smilodox:secret-sale:probe-pending-persist";
  const STORAGE_TAB_OWNER = "smilodox:secret-sale:tab-owner";
  const EVENT_STATE = "smilodox:secret-sale:probe-state";
  const SCRUB_MAX_ATTEMPTS = 4;
  const RECOVERY_STALE_MS = 15000;
  const RECOVERY_HEARTBEAT_MS = 5000;

  let probeInFlight = false;
  let probeScrubInFlight = false;
  let probeCleanupUnconfirmed = false;
  let checkoutWaitInFlight = false;
  let checkoutWaitTrigger = null;
  let checkoutPassthrough = false;
  let configuredVariantId = null;
  let checkoutBound = false;
  let activeProbeToken = null;
  let recoveryInFlight = false;
  let lastSeenProbeLines = false;
  let recoveryHeartbeatTimer = null;

  function normalizeCode(raw) {
    return String(raw || "")
      .trim()
      .replace(/\s+/g, "")
      .toUpperCase();
  }

  function shopRoot() {
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
      return String(root).replace(/\/?$/, "/");
    } catch (e) {
      return "/";
    }
  }

  function cartJsUrl() {
    return shopRoot() + "cart.js";
  }

  function cartAddUrl() {
    return shopRoot() + "cart/add.js";
  }

  function cartChangeUrl() {
    return shopRoot() + "cart/change.js";
  }

  function cartUpdateUrl() {
    return shopRoot() + "cart/update.js";
  }

  function sleep(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function createProbeToken() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === "function") {
        return window.crypto.randomUUID();
      }
    } catch (e) {}
    return "p-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
  }

  function createProbeNetworkError(message) {
    const err = new Error(message || "Probe network error");
    err.name = "SmilodoxSecretSaleProbeNetworkError";
    return err;
  }

  function isProbeNetworkError(error) {
    return error && error.name === "SmilodoxSecretSaleProbeNetworkError";
  }

  function writeValidationResult(code, result) {
    const key = normalizeCode(code);
    if (!key) return;
    try {
      sessionStorage.setItem(STORAGE_VALIDATION_PREFIX + key, String(result || ""));
      window.dispatchEvent(
        new CustomEvent(EVENT_STATE, {
          detail: { code: key, validation: result },
        }),
      );
    } catch (e) {}
  }

  function readValidationResult(code) {
    const key = normalizeCode(code);
    if (!key) return "";
    try {
      return sessionStorage.getItem(STORAGE_VALIDATION_PREFIX + key) || "";
    } catch (e) {
      return "";
    }
  }

  function markProbeDone(code) {
    const key = normalizeCode(code);
    if (!key) return;
    try {
      sessionStorage.setItem(STORAGE_PROBE_DONE_PREFIX + key, "1");
    } catch (e) {}
  }

  function isProbeDone(code) {
    const key = normalizeCode(code);
    if (!key) return false;
    try {
      return sessionStorage.getItem(STORAGE_PROBE_DONE_PREFIX + key) === "1";
    } catch (e) {
      return false;
    }
  }

  function clearProbeArtifacts(code) {
    const key = normalizeCode(code);
    if (!key) return;
    try {
      sessionStorage.removeItem(STORAGE_VALIDATION_PREFIX + key);
      sessionStorage.removeItem(STORAGE_PROBE_DONE_PREFIX + key);
    } catch (e) {}
  }

  function getTabOwnerId() {
    try {
      let id = sessionStorage.getItem(STORAGE_TAB_OWNER);
      if (id) return id;
      id = createProbeToken();
      sessionStorage.setItem(STORAGE_TAB_OWNER, id);
      return id;
    } catch (e) {
      return "t-anon";
    }
  }

  function readPersistRecovery() {
    try {
      const raw = localStorage.getItem(STORAGE_RECOVERY_PERSIST);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function writePersistRecovery(token) {
    try {
      localStorage.setItem(
        STORAGE_RECOVERY_PERSIST,
        JSON.stringify({
          token: String(token || "1"),
          at: Date.now(),
          owner: getTabOwnerId(),
        }),
      );
    } catch (e) {}
  }

  function stopRecoveryHeartbeat() {
    if (recoveryHeartbeatTimer != null) {
      window.clearInterval(recoveryHeartbeatTimer);
      recoveryHeartbeatTimer = null;
    }
  }

  function startRecoveryHeartbeat(token) {
    stopRecoveryHeartbeat();
    const t = String(token || "1");
    recoveryHeartbeatTimer = window.setInterval(function () {
      if (!readSessionRecoveryMarker()) {
        stopRecoveryHeartbeat();
        return;
      }
      writePersistRecovery(t);
    }, RECOVERY_HEARTBEAT_MS);
  }

  function readSessionRecoveryMarker() {
    try {
      return sessionStorage.getItem(STORAGE_RECOVERY_MARKER) || "";
    } catch (e) {
      return "";
    }
  }

  function writeRecoveryMarker(token) {
    const t = String(token || "1");
    try {
      sessionStorage.setItem(STORAGE_RECOVERY_MARKER, t);
    } catch (e) {}
    writePersistRecovery(t);
    startRecoveryHeartbeat(t);
  }

  function readRecoveryMarker() {
    const session = readSessionRecoveryMarker();
    if (session) return session;
    const persist = readPersistRecovery();
    if (!persist || !persist.token) return "";
    if (String(persist.owner || "") === String(getTabOwnerId())) return String(persist.token);
    const at = Number(persist.at || 0);
    if (!at || Date.now() - at >= RECOVERY_STALE_MS) return String(persist.token);
    return "";
  }

  /**
   * Clear markers for this probe only. Never wipe a newer persist token from
   * another tab’s in-flight probe.
   */
  function clearRecoveryMarker(expectedToken) {
    stopRecoveryHeartbeat();
    const expected =
      expectedToken != null && String(expectedToken) !== "" ? String(expectedToken) : "";
    try {
      const session = sessionStorage.getItem(STORAGE_RECOVERY_MARKER) || "";
      if (!expected || !session || session === expected) {
        sessionStorage.removeItem(STORAGE_RECOVERY_MARKER);
      }
    } catch (e) {}
    try {
      const persist = readPersistRecovery();
      if (!persist) return;
      if (expected && String(persist.token || "") !== expected) return;
      localStorage.removeItem(STORAGE_RECOVERY_PERSIST);
    } catch (e) {}
  }

  function shouldAttemptRecovery() {
    if (readSessionRecoveryMarker()) return true;
    const persist = readPersistRecovery();
    if (!persist || !persist.token) return false;
    if (String(persist.owner || "") === String(getTabOwnerId())) return true;
    const at = Number(persist.at || 0);
    if (!at) return true;
    return Date.now() - at >= RECOVERY_STALE_MS;
  }

  function hasRecoveryMarker() {
    return shouldAttemptRecovery();
  }

  let staleRecoveryWatchTimer = null;

  function scheduleStaleRecoveryWatch() {
    if (staleRecoveryWatchTimer != null) {
      window.clearTimeout(staleRecoveryWatchTimer);
      staleRecoveryWatchTimer = null;
    }
    if (probeInFlight || recoveryInFlight) return;

    if (shouldAttemptRecovery()) {
      recoverStrandedProbeLines();
      return;
    }

    const persist = readPersistRecovery();
    if (!persist || !persist.token) return;
    if (String(persist.owner || "") === String(getTabOwnerId())) return;

    const at = Number(persist.at || 0);
    if (!at) {
      recoverStrandedProbeLines();
      return;
    }

    const wait = RECOVERY_STALE_MS - (Date.now() - at);
    if (wait <= 0) {
      recoverStrandedProbeLines();
      return;
    }

    staleRecoveryWatchTimer = window.setTimeout(function () {
      staleRecoveryWatchTimer = null;
      scheduleStaleRecoveryWatch();
    }, Math.min(wait + 250, RECOVERY_STALE_MS + 250));
  }

  function storeCandidate(code) {
    const key = normalizeCode(code);
    if (!key) return;
    try {
      sessionStorage.setItem(STORAGE_CANDIDATE, key);
    } catch (e) {}
  }

  function readCandidate() {
    try {
      return normalizeCode(sessionStorage.getItem(STORAGE_CANDIDATE) || "");
    } catch (e) {
      return "";
    }
  }

  async function fetchCart() {
    const response = await fetch(cartJsUrl(), {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      cache: "no-store",
    });
    if (!response.ok) throw createProbeNetworkError("cart.js failed");
    return response.json();
  }

  function findCartDiscountEntry(cart, displayCode) {
    const normalized = normalizeCode(displayCode);
    if (!normalized || !cart) return null;
    return (
      (cart.discount_codes || []).find(function (dc) {
        return dc && dc.code && normalizeCode(dc.code) === normalized;
      }) || null
    );
  }

  function evaluateProbeSuccess(cart, displayCode) {
    const matching = findCartDiscountEntry(cart, displayCode);
    if (!matching) return false;
    if (matching.applicable === false) return false;
    if (matching.applicable === true) return true;
    return false;
  }

  function probePropertyValue(item) {
    const props = (item && item.properties) || {};
    return props[PROBE_LINE_PROPERTY];
  }

  function isAnyProbeLineItem(item) {
    const marker = probePropertyValue(item);
    return marker != null && marker !== "" && marker !== false;
  }

  function isProbeLineForToken(item, token) {
    if (!token) return false;
    return String(probePropertyValue(item) || "") === String(token);
  }

  function cartHasAnyProbeLines(cart) {
    const present = Boolean((cart && cart.items ? cart.items : []).some(isAnyProbeLineItem));
    if (cart && cart.items) lastSeenProbeLines = present;
    return present;
  }

  function cartHasProbeToken(cart, token) {
    if (!token) return cartHasAnyProbeLines(cart);
    return Boolean(
      (cart && cart.items ? cart.items : []).some(function (item) {
        return isProbeLineForToken(item, token);
      }),
    );
  }

  function rememberProbeVariantId(variantId) {
    if (!variantId) return;
    try {
      sessionStorage.setItem(STORAGE_PROBE_VARIANT, String(variantId));
    } catch (e) {}
  }

  function clearProbeVariantId() {
    try {
      sessionStorage.removeItem(STORAGE_PROBE_VARIANT);
    } catch (e) {}
  }

  function resolveProbeVariantId() {
    if (configuredVariantId) return String(configuredVariantId);
    try {
      const stored = sessionStorage.getItem(STORAGE_PROBE_VARIANT);
      if (stored) return String(stored);
    } catch (e) {}
    return null;
  }

  async function addProbeLine(variantId, token) {
    let response;
    try {
      response = await fetch(cartAddUrl(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: {
                [PROBE_LINE_PROPERTY]: String(token),
              },
            },
          ],
        }),
      });
    } catch (e) {
      throw createProbeNetworkError("Probe line add failed");
    }

    if (!response.ok) {
      throw createProbeNetworkError("Probe line add failed");
    }

    const data = await response.json();
    let lineKeys = (data.items || [])
      .filter(function (item) {
        return isProbeLineForToken(item, token);
      })
      .map(function (item) {
        return item.key;
      })
      .filter(Boolean);

    // Never fall back to unmarked keys (protects customer lines of the same variant).
    if (!lineKeys.length) {
      const cart = await fetchCart().catch(function () {
        return null;
      });
      lineKeys = ((cart && cart.items) || [])
        .filter(function (item) {
          return isProbeLineForToken(item, token);
        })
        .map(function (item) {
          return item.key;
        })
        .filter(Boolean);
    }

    if (!lineKeys.length) {
      throw createProbeNetworkError("Probe line key missing from cart add response");
    }

    rememberProbeVariantId(variantId);
    return lineKeys;
  }

  async function removeLineKey(lineKey) {
    if (!lineKey) return;
    try {
      await fetch(cartChangeUrl(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
        }),
      });
    } catch (e) {
      /* continue scrubbing */
    }
  }

  async function removeProbeLines(cart, options) {
    const opts = options || {};
    const token = opts.token || null;
    const knownKeys = opts.lineKeys || [];
    const keysToRemove = new Set();
    const items = (cart && cart.items) || [];

    function itemByKey(key) {
      return items.find(function (item) {
        return item && item.key === key;
      });
    }

    // Only remove lines that still carry the probe property. Known keys alone are
    // never enough — a customer's own Amaze Pro (same variant) must stay untouched.
    knownKeys.forEach(function (key) {
      if (!key) return;
      const item = itemByKey(key);
      if (!item) return;
      if (token) {
        if (isProbeLineForToken(item, token)) keysToRemove.add(key);
      } else if (isAnyProbeLineItem(item)) {
        keysToRemove.add(key);
      }
    });

    items.forEach(function (item) {
      if (!item || !item.key) return;
      if (token) {
        if (isProbeLineForToken(item, token)) keysToRemove.add(item.key);
      } else if (isAnyProbeLineItem(item)) {
        keysToRemove.add(item.key);
      }
    });

    for (const lineKey of keysToRemove) {
      await removeLineKey(lineKey);
    }
  }

  /**
   * Force-capable scrub. Verifies cart is clean. Does not refuse while probeInFlight
   * when force=true (required for probe finally cleanup).
   */
  async function ensureCartWithoutProbeLines(cart, options) {
    const opts = options || {};
    const force = opts.force === true;
    const token = opts.token || null;
    const lineKeys = opts.lineKeys || [];

    if (!force && probeScrubInFlight) {
      return { cart: cart || null, clean: !cartHasAnyProbeLines(cart) };
    }

    probeScrubInFlight = true;
    let working = cart || null;
    let clean = false;

    try {
      for (let attempt = 0; attempt < SCRUB_MAX_ATTEMPTS; attempt += 1) {
        if (!working) {
          working = await fetchCart().catch(function () {
            return null;
          });
        }

        const hasTarget = token ? cartHasProbeToken(working, token) : cartHasAnyProbeLines(working);
        if (!hasTarget && !cartHasAnyProbeLines(working)) {
          clean = true;
          break;
        }

        await removeProbeLines(working, { token: token, lineKeys: lineKeys });
        working = await fetchCart().catch(function () {
          return null;
        });

        if (working && !cartHasAnyProbeLines(working)) {
          clean = true;
          break;
        }
      }

      if (clean) {
        probeCleanupUnconfirmed = false;
        clearRecoveryMarker(token || readSessionRecoveryMarker());
        clearProbeVariantId();
      } else {
        probeCleanupUnconfirmed = true;
      }

      return { cart: working, clean: clean };
    } finally {
      probeScrubInFlight = false;
      // After scrub flag clears so Snow’s cart-drawer can honor cart:refresh.
      // Caller must clear probeInFlight before relying on this when scrubbing
      // from the normal validation finally path.
      if (clean && working && !probeInFlight) publishCartRefresh(working);
    }
  }

  function publishCartRefresh(cart) {
    if (probeInFlight || probeScrubInFlight) return;
    try {
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:updated", {
          bubbles: true,
          detail: { cart: cart },
        }),
      );
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:refresh", {
          bubbles: true,
          detail: { cart: cart },
        }),
      );
    } catch (e) {}
  }

  async function applyCodeToCart(displayCode, cart) {
    const trimmed = String(displayCode || "").trim();
    if (!trimmed) return cart || null;

    const existing = ((cart && cart.discount_codes) || [])
      .map(function (entry) {
        return entry && entry.code ? String(entry.code).trim() : "";
      })
      .filter(Boolean);

    const alreadyPresent = existing.some(function (code) {
      return normalizeCode(code) === normalizeCode(trimmed);
    });

    if (alreadyPresent) {
      return (
        cart ||
        (await fetchCart().catch(function () {
          return null;
        }))
      );
    }

    const formData = new FormData();
    formData.append("discount", existing.concat([trimmed]).join(","));

    let response;
    try {
      response = await fetch(cartUpdateUrl(), {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
        body: formData,
      });
    } catch (e) {
      throw createProbeNetworkError("Probe cart sync failed");
    }

    if (!response.ok) {
      throw createProbeNetworkError("Probe cart sync failed");
    }

    return fetchCart();
  }

  async function runEmptyCartProbe(displayCode) {
    const code = normalizeCode(displayCode);
    if (!code) return "rejected";

    if (probeInFlight) {
      return readValidationResult(code) || "pending";
    }

    const cached = readValidationResult(code);
    if (isProbeDone(code) && (cached === "activated" || cached === "rejected")) {
      return cached;
    }

    const variantId = resolveProbeVariantId();
    if (!variantId) {
      writeValidationResult(code, "pending");
      return "pending";
    }

    storeCandidate(code);
    const token = createProbeToken();
    activeProbeToken = token;
    // Persist before the temporary line exists so a mid-probe navigation still
    // wakes the dormant global guard on cart/PDP/other pages.
    writeRecoveryMarker(token);
    probeInFlight = true;
    writeValidationResult(code, "checking");

    let cart = null;
    let probeLineKeys = [];
    let result = "pending";

    try {
      probeLineKeys = await addProbeLine(variantId, token);
      cart = await applyCodeToCart(code, cart);
      if (!cart) {
        throw createProbeNetworkError("Probe cart sync returned empty cart");
      }

      const success = evaluateProbeSuccess(cart, code);
      result = success ? "activated" : "rejected";
      writeValidationResult(code, result);
      markProbeDone(code);
    } catch (error) {
      console.warn("Smilodox Secret Sale probe failed", error);
      if (isProbeNetworkError(error)) {
        result = "pending";
        writeValidationResult(code, "pending");
      } else {
        result = "rejected";
        writeValidationResult(code, "rejected");
        markProbeDone(code);
      }
    } finally {
      let scrubbedClean = false;
      try {
        cart = await fetchCart().catch(function () {
          return null;
        });
        const scrub = await ensureCartWithoutProbeLines(cart, {
          force: true,
          token: token,
          lineKeys: probeLineKeys,
        });
        cart = scrub.cart;
        scrubbedClean = scrub.clean;
      } catch (e) {
        probeCleanupUnconfirmed = true;
      }
      // Clear in-flight before refresh — otherwise publishCartRefresh discards it
      // and the open cart drawer keeps showing the temporary probe line.
      activeProbeToken = null;
      probeInFlight = false;
      if (scrubbedClean && cart) publishCartRefresh(cart);
      writeValidationResult(code, result);
    }

    return result;
  }

  async function validateCode(displayCode, options) {
    const opts = options || {};
    const code = normalizeCode(displayCode);
    if (!code) return "rejected";

    if (opts.variantId) {
      configuredVariantId = String(opts.variantId).trim() || configuredVariantId;
    }

    storeCandidate(code);

    const delays = opts.delays || [0, 200, 500, 1000, 2000];
    let last = "pending";

    for (let i = 0; i < delays.length; i += 1) {
      if (delays[i] > 0) await sleep(delays[i]);

      try {
        const cart = await fetchCart();
        if (cartHasAnyProbeLines(cart)) {
          await ensureCartWithoutProbeLines(cart, { force: true });
        }

        const fresh = await fetchCart().catch(function () {
          return cart;
        });
        const realLines = (fresh.items || []).filter(function (item) {
          return !isAnyProbeLineItem(item);
        }).length;

        if (realLines > 0) {
          const entry = findCartDiscountEntry(fresh, code);
          if (entry && entry.applicable === true) {
            writeValidationResult(code, "activated");
            markProbeDone(code);
            return "activated";
          }
        }
      } catch (e) {
        /* continue to probe */
      }

      last = await runEmptyCartProbe(code);
      if (last === "activated" || last === "rejected") return last;
    }

    return last;
  }

  function setCheckoutControlBusy(trigger, busy) {
    if (!(trigger instanceof Element)) return;
    try {
      if (busy) trigger.setAttribute("aria-busy", "true");
      else trigger.removeAttribute("aria-busy");
    } catch (e) {}
  }

  function clearCheckoutWaitState() {
    setCheckoutControlBusy(checkoutWaitTrigger, false);
    checkoutWaitTrigger = null;
    checkoutWaitInFlight = false;
  }

  function resolveCheckoutForm(trigger) {
    if (trigger instanceof HTMLFormElement) return trigger;
    if (trigger && trigger.form) return trigger.form;
    if (trigger && trigger.closest) {
      const near = trigger.closest("form");
      if (near) return near;
    }
    return null;
  }

  function isCheckoutBlocking() {
    return Boolean(
      probeInFlight ||
        probeScrubInFlight ||
        probeCleanupUnconfirmed ||
        hasRecoveryMarker(),
    );
  }

  function formactionLooksLikeCheckout(value) {
    return /\/checkout(\/|\?|$)/i.test(String(value || ""));
  }

  function isGenuineCheckoutControl(el) {
    if (!(el instanceof Element)) return false;
    if (el.matches('button[name="checkout"], input[name="checkout"]')) return true;
    if (el.getAttribute("name") === "checkout") return true;
    if (formactionLooksLikeCheckout(el.getAttribute("formaction"))) return true;
    return false;
  }

  function isNonCheckoutCartControl(el) {
    if (!(el instanceof Element)) return false;
    if (el.matches('button[name="update"], input[name="update"]')) return true;
    if (el.matches('button[name="add"], input[name="add"]')) return true;
    if (el.getAttribute("name") === "update" || el.getAttribute("name") === "add") return true;
    const formaction = el.getAttribute("formaction") || "";
    if (formaction && !formactionLooksLikeCheckout(formaction) && /\/cart/i.test(formaction)) {
      return true;
    }
    return false;
  }

  /**
   * True only for genuine checkout intent. Ordinary /cart quantity/update submits
   * must never be treated as checkout.
   */
  function isGenuineCheckoutSubmit(event, form) {
    if (!(form instanceof HTMLFormElement)) return false;

    const submitter = event && event.submitter instanceof Element ? event.submitter : null;
    if (submitter) {
      if (isNonCheckoutCartControl(submitter)) return false;
      if (isGenuineCheckoutControl(submitter)) return true;
      if (formactionLooksLikeCheckout(submitter.getAttribute("formaction"))) return true;
      return false;
    }

    const action = form.getAttribute("action") || "";
    if (formactionLooksLikeCheckout(action)) return true;

    // Keyboard submit without submitter: only if the form itself targets checkout.
    // Do not treat generic /cart forms as checkout.
    return false;
  }

  function shouldInterceptCheckout() {
    // Intercept only when probe/scrub is active, cleanup is unconfirmed, or we last
    // observed a marked probe line. Ordinary cart updates never hit this path.
    return isCheckoutBlocking() || lastSeenProbeLines;
  }

  /**
   * Resume via the original control/path so theme and app submit listeners still run.
   * Narrow passthrough flag prevents re-entering the guard.
   */
  function resumeCheckoutThroughOriginalPath(trigger) {
    checkoutPassthrough = true;
    try {
      if (trigger instanceof HTMLButtonElement || trigger instanceof HTMLInputElement) {
        if (typeof trigger.click === "function") {
          trigger.click();
          return;
        }
      }

      const form = resolveCheckoutForm(trigger);
      if (form && typeof form.requestSubmit === "function") {
        if (trigger instanceof HTMLElement && form.contains(trigger)) {
          form.requestSubmit(trigger);
        } else {
          const checkoutBtn = form.querySelector('button[name="checkout"], input[name="checkout"]');
          if (checkoutBtn) form.requestSubmit(checkoutBtn);
          else form.requestSubmit();
        }
        return;
      }

      if (form) {
        // Last resort only — prefer requestSubmit/click above.
        HTMLFormElement.prototype.submit.call(form);
        return;
      }

      window.location.assign(shopRoot() + "checkout");
    } finally {
      window.setTimeout(function () {
        checkoutPassthrough = false;
      }, 0);
    }
  }

  async function resumeCheckoutAfterScrub(trigger) {
    let cart = await fetchCart().catch(function () {
      return null;
    });

    if (cartHasAnyProbeLines(cart) || probeCleanupUnconfirmed || probeInFlight || probeScrubInFlight) {
      const scrub = await ensureCartWithoutProbeLines(cart, { force: true });
      cart = scrub.cart;
      if (!scrub.clean) {
        console.warn("Smilodox Secret Sale: checkout blocked — probe line still in cart");
        return;
      }
    }

    const recheck = await fetchCart().catch(function () {
      return null;
    });
    if (cartHasAnyProbeLines(recheck)) {
      console.warn("Smilodox Secret Sale: checkout blocked — probe line still in cart");
      probeCleanupUnconfirmed = true;
      return;
    }

    resumeCheckoutThroughOriginalPath(trigger);
  }

  function beginCheckoutWait(event, trigger) {
    if (checkoutPassthrough) return;
    if (!shouldInterceptCheckout()) return;

    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === "function") {
      event.stopImmediatePropagation();
    }

    if (checkoutWaitInFlight) return;
    checkoutWaitInFlight = true;
    checkoutWaitTrigger = trigger || event.target;
    setCheckoutControlBusy(checkoutWaitTrigger, true);

    (async function () {
      const startedAt = Date.now();
      const hardWaitMs = 60000;
      try {
        while (probeInFlight || probeScrubInFlight) {
          if (Date.now() - startedAt > hardWaitMs) return;
          await sleep(50);
        }
        await resumeCheckoutAfterScrub(checkoutWaitTrigger);
      } finally {
        clearCheckoutWaitState();
      }
    })().catch(function () {
      clearCheckoutWaitState();
    });
  }

  function handleCheckoutClick(event) {
    if (checkoutPassthrough) return;
    const trigger =
      event.target instanceof Element
        ? event.target.closest(
            'button[name="checkout"], input[name="checkout"], [name="checkout"], [formaction*="checkout"]',
          )
        : null;
    if (!trigger || !isGenuineCheckoutControl(trigger)) return;
    if (isNonCheckoutCartControl(trigger)) return;

    beginCheckoutWait(event, trigger);
  }

  function handleCheckoutSubmit(event) {
    if (checkoutPassthrough) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!isGenuineCheckoutSubmit(event, form)) return;

    const trigger =
      event.submitter instanceof Element
        ? event.submitter
        : form.querySelector('button[name="checkout"], input[name="checkout"]') || form;

    beginCheckoutWait(event, trigger);
  }

  async function recoverStrandedProbeLines() {
    if (recoveryInFlight || probeInFlight) return;
    if (!shouldAttemptRecovery()) return;

    recoveryInFlight = true;
    // Block genuine checkout before the first cart fetch (fast-checkout race).
    probeCleanupUnconfirmed = true;
    bindCheckoutGuard();

    // Claim this tab (session + refreshed persist) so peer tabs stay out.
    const persist = readPersistRecovery();
    const claimToken =
      readSessionRecoveryMarker() || (persist && persist.token) || activeProbeToken || "1";
    writeRecoveryMarker(claimToken);

    try {
      const cart = await fetchCart().catch(function () {
        return null;
      });
      if (!cartHasAnyProbeLines(cart)) {
        probeCleanupUnconfirmed = false;
        clearRecoveryMarker(claimToken);
        lastSeenProbeLines = false;
        return;
      }
      // Scrub any marked probe lines (property-only); clear only our claim token.
      const scrub = await ensureCartWithoutProbeLines(cart, { force: true });
      if (scrub.clean) {
        lastSeenProbeLines = false;
        clearRecoveryMarker(claimToken);
        if (scrub.cart) publishCartRefresh(scrub.cart);
      }
    } catch (e) {
      probeCleanupUnconfirmed = true;
    } finally {
      recoveryInFlight = false;
      scheduleStaleRecoveryWatch();
    }
  }

  function bindCheckoutGuard() {
    if (checkoutBound) return;
    checkoutBound = true;
    document.addEventListener("click", handleCheckoutClick, true);
    document.addEventListener("submit", handleCheckoutSubmit, true);
  }

  function configure(options) {
    const opts = options || {};
    if (opts.variantId) {
      configuredVariantId = String(opts.variantId).trim() || null;
    }
    // Gate pages always bind: in-session probe may start without a prior marker.
    bindCheckoutGuard();
    if (shouldAttemptRecovery()) {
      recoverStrandedProbeLines();
    }
  }

  function isBusy() {
    return Boolean(
      probeInFlight || probeScrubInFlight || probeCleanupUnconfirmed || hasRecoveryMarker(),
    );
  }

  /**
   * When this full probe is loaded (section or on-demand bootstrap) and a
   * recoverable marker exists, arm checkout guard and scrub marked lines.
   */
  function bootIfRecoveryNeeded() {
    if (!shouldAttemptRecovery()) return;
    probeCleanupUnconfirmed = true;
    bindCheckoutGuard();
    function startRecovery() {
      recoverStrandedProbeLines();
    }
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", startRecovery);
    } else {
      startRecovery();
    }
  }

  window.SmilodoxSecretSaleProbe = {
    PROBE_LINE_PROPERTY: PROBE_LINE_PROPERTY,
    STORAGE_RECOVERY_MARKER: STORAGE_RECOVERY_MARKER,
    STORAGE_RECOVERY_PERSIST: STORAGE_RECOVERY_PERSIST,
    RECOVERY_STALE_MS: RECOVERY_STALE_MS,
    configure: configure,
    validateCode: validateCode,
    runEmptyCartProbe: runEmptyCartProbe,
    readValidationResult: readValidationResult,
    writeValidationResult: writeValidationResult,
    clearProbeArtifacts: clearProbeArtifacts,
    isProbeDone: isProbeDone,
    storeCandidate: storeCandidate,
    readCandidate: readCandidate,
    normalizeCode: normalizeCode,
    isBusy: isBusy,
    isCheckoutBlocking: isCheckoutBlocking,
    fetchCart: fetchCart,
    recoverStrandedProbeLines: recoverStrandedProbeLines,
    ensureCartWithoutProbeLines: ensureCartWithoutProbeLines,
    cartHasAnyProbeLines: cartHasAnyProbeLines,
    isAnyProbeLineItem: isAnyProbeLineItem,
    hasRecoveryMarker: hasRecoveryMarker,
    shouldAttemptRecovery: shouldAttemptRecovery,
    scheduleStaleRecoveryWatch: scheduleStaleRecoveryWatch,
    readRecoveryMarker: readRecoveryMarker,
    clearRecoveryMarker: clearRecoveryMarker,
    EVENT_STATE: EVENT_STATE,
  };

  bootIfRecoveryNeeded();
  scheduleStaleRecoveryWatch();
  try {
    window.addEventListener("storage", function (event) {
      if (!event || event.key !== STORAGE_RECOVERY_PERSIST) return;
      scheduleStaleRecoveryWatch();
    });
  } catch (e) {}
})();
